import { useEffect, useRef } from "react";
import { supabase } from "../services/supabase";
import { useAuthStore } from "../store/useAuthStore";
import { APP_FOREGROUND_HEARTBEAT_MS } from "../config/publicConfig";

/**
 * 标签页可见时节流更新 profiles.last_app_foreground_at（与移动端前台心跳语义对齐）。
 */
export function useAppForegroundHeartbeat(enabled: boolean) {
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const ping = async () => {
    if (typeof document !== "undefined" && document.visibilityState !== "visible") {
      return;
    }
    const user = useAuthStore.getState().user ?? (await supabase.auth.getSession()).data.session?.user;
    if (!user) return;
    await supabase
      .from("profiles")
      .update({ last_app_foreground_at: new Date().toISOString() })
      .eq("id", user.id);
  };

  useEffect(() => {
    if (!enabled) return;

    const start = () => {
      if (timerRef.current) return;
      void ping();
      timerRef.current = setInterval(() => void ping(), APP_FOREGROUND_HEARTBEAT_MS);
    };

    const stop = () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    };

    const onVisibility = () => {
      if (document.visibilityState === "visible") {
        start();
      } else {
        stop();
      }
    };

    onVisibility();
    document.addEventListener("visibilitychange", onVisibility);

    return () => {
      document.removeEventListener("visibilitychange", onVisibility);
      stop();
    };
  }, [enabled]);
}
